import { useState, useMemo, useEffect } from "react";
import { serviceColors } from "./Mockdata";
import "./Admin.css";

/* ─── tiny reusable chart components (no library needed) ─── */

function DonutChart({ segments, size = 140 }) {
  const r = 50, cx = 70, cy = 70;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const total = segments.reduce((s, g) => s + g.value, 0);
  return (
    <svg width={size} height={size} viewBox="0 0 140 140">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f4f3" strokeWidth="22" />
      {segments.map((seg, i) => {
        const pct  = total ? seg.value / total : 0;
        const dash = pct * circ;
        const gap  = circ - dash;
        const el = (
          <circle
            key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={seg.color} strokeWidth="22"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            style={{ transform: "rotate(-90deg)", transformOrigin: "70px 70px", transition: "stroke-dasharray 0.6s ease" }}
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize="22" fontWeight="700" fill="#0f1f1d">{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="#7a9694">Total</text>
    </svg>
  );
}

function BarChart({ data, height = 120 }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="bar-chart" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="bar-col">
          <div className="bar-value">{d.value}</div>
          <div
            className="bar-fill"
            style={{
              height: `${(d.value / max) * 80}%`,
              background: d.color,
              animationDelay: `${i * 0.08}s`,
            }}
          />
          <div className="bar-label">{d.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Status badge ─── */
const STATUS_LABELS = { pending:"Pending", confirmed:"Confirmed", cancelled:"Cancelled" };
const STATUS_ICONS  = { pending:"⏳", confirmed:"✅", cancelled:"❌" };

/* ─── Appointment row ─── */
function AppointmentRow({ appt, onConfirm, onCancel }) {
  const [expanded, setExpanded] = useState(false);
  const [selSlot, setSelSlot] = useState(
  appt.status === "confirmed" ? (appt.confirmedSlot || "") : ""
);
  const sc = serviceColors[appt.service] || serviceColors["Other"];

  const timeAgo = (iso) => {
    const diff = Math.floor((Date.now() - new Date(iso)) / 60000);
    if (diff < 60)   return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
    return `${Math.floor(diff/1440)}d ago`;
  };

  return (
    <>
      <tr className={`apt-row ${appt.status} ${expanded ? "expanded" : ""}`} onClick={() => setExpanded(e => !e)}>
        <td><span className="apt-id">{appt.id}</span></td>
        <td>
          <div className="apt-name">{appt.name}</div>
          <div className="apt-phone">{appt.phone}</div>
          <div className="apt-email">{appt.email}</div>
        </td>
        <td>
          <span className="service-badge" style={{ background: sc.bg, color: sc.color }}>{appt.service}</span>
          {appt.reason && <div className="apt-reason">↳ {appt.reason}</div>}
        </td>
        <td>
          <div className="apt-date">📅 {appt.date}</div>
          <div className="apt-slots-preview">
            {appt.slots.slice(0,2).map(s => <span key={s} className="slot-mini">{s}</span>)}
            {appt.slots.length > 2 && <span className="slot-mini">+{appt.slots.length-2}</span>}
          </div>
        </td>
        <td><span className={`status-badge status-${appt.status}`}>{STATUS_ICONS[appt.status]} {STATUS_LABELS[appt.status]}</span></td>
        <td><span className="time-ago">{timeAgo(appt.createdAt)}</span></td>
        <td>
          <div className="apt-actions" onClick={e => e.stopPropagation()}>
            {(appt.status === "pending" || appt.status === "cancelled") && (
              <>
                <button
                  className="act-btn act-confirm"
                  onClick={() => onConfirm(appt.id, selSlot)}
                >
                  ✓ Confirm
                </button>
                <button
                  className="act-btn act-cancel"
                  onClick={() => onCancel(appt.id)}
                >
                  ✕ Cancel
                </button>
              </>
            )}

            {appt.status === "confirmed" && (
              <button
                className="act-btn act-cancel"
                onClick={() => onCancel(appt.id)}
              >
                ✕ Cancel
              </button>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="apt-detail-row">
          <td colSpan={7}>
            <div className="apt-detail">
              <div className="detail-section">
                <div className="detail-label">Patient Info</div>
                <div className="detail-grid">
                  <span>Name</span><strong>{appt.name}</strong>
                  <span>Phone</span><strong>{appt.phone}</strong>
                  <span>email</span><strong>{appt.email}</strong>
                  <span>Service</span><strong>{appt.service}{appt.reason ? ` — ${appt.reason}` : ""}</strong>
                </div>
              </div>
              <div className="detail-section">
                <div className="detail-label">Preferred Slots</div>
                <div className="slot-select-row">
                  {appt.slots.map((s,i) => (
                    <label key={s} className={`slot-radio ${selSlot===s?"active":""}`}>
                      <input type="radio" name={`slot-${appt.id}`} value={s} checked={selSlot===s}
                        onChange={() => setSelSlot(s)} disabled={!(appt.status === "pending" || appt.status === "cancelled")} />
                      <span>#{i+1}</span> {s}
                    </label>
                  ))}
                </div>
              </div>
              {appt.message && (
                <div className="detail-section">
                  <div className="detail-label">Patient Note</div>
                  <div className="detail-note">"{appt.message}"</div>
                </div>
              )}
              {(appt.status === "pending" || appt.status === "cancelled") && (
                <div className="detail-confirm-row">
                  <button
                    className="act-btn act-confirm"
                    disabled={!(selSlot)}
                    onClick={() => onConfirm(appt.id, selSlot)}
                  >
                    ✓ Confirm with {selSlot || "selected slot"}
                  </button>
                </div>
              )}
              {appt.status === "confirmed" && (
                <div className="detail-confirmed-note">✅ Confirmed for <strong>{appt.confirmedSlot}</strong></div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ─── Services Manager ─── */
const DEFAULT_SERVICES = [
  { id:1, name:"Dental Implants",      icon:"🦷", active:true,  duration:"90 min", price:"₹25,000+" },
  { id:2, name:"Root Canal Treatment", icon:"🔬", active:true,  duration:"60 min", price:"₹8,000+"  },
  { id:3, name:"Cosmetic Dentistry",   icon:"✨", active:true,  duration:"45 min", price:"₹5,000+"  },
  { id:4, name:"Teeth Cleaning",       icon:"🧹", active:true,  duration:"30 min", price:"₹1,500+"  },
  { id:5, name:"Dental Braces",        icon:"🔲", active:true,  duration:"30 min", price:"₹20,000+" },
  { id:6, name:"Oral Surgery",         icon:"🏥", active:true,  duration:"60 min", price:"₹12,000+" },
  { id:7, name:"General Checkup",      icon:"📋", active:true,  duration:"20 min", price:"₹500+"    },
];

const ICON_OPTIONS = ["🦷","🔬","✨","🧹","🔲","🏥","📋","💊","🩺","🫀","😁","💉","🩻","🧪","🩹"];

function ServicesManager() {
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [editing,  setEditing]  = useState(null); // id being edited
  const [showAdd,  setShowAdd]  = useState(false);
  const [newSvc,   setNewSvc]   = useState({ name:"", icon:"🦷", duration:"", price:"", active:true });
  const [toast,    setToast]    = useState(null);

  const flash = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const toggleActive = (id) => {
    setServices(s => s.map(sv => sv.id===id ? {...sv, active:!sv.active} : sv));
    flash("Service status updated");
  };

  const deleteService = (id) => {
    setServices(s => s.filter(sv => sv.id!==id));
    flash("Service removed", "error");
  };

  const saveEdit = (id, updated) => {
    setServices(s => s.map(sv => sv.id===id ? {...sv,...updated} : sv));
    setEditing(null);
    flash("Service updated");
  };

  const addService = () => {
    if (!newSvc.name.trim()) return;
    setServices(s => [...s, { ...newSvc, id: Date.now() }]);
    setNewSvc({ name:"", icon:"🦷", duration:"", price:"", active:true });
    setShowAdd(false);
    flash("New service added ✓");
  };

  return (
    <div className="svc-manager">
      {toast && <div className={`admin-toast ${toast.type}`}>{toast.type==="success"?"✓":"✕"} {toast.msg}</div>}

      <div className="svc-header">
        <div>
          <h2 className="svc-title">Manage Services</h2>
          <p className="svc-sub">{services.filter(s=>s.active).length} active · {services.filter(s=>!s.active).length} hidden</p>
        </div>
        <button className="svc-add-btn" onClick={() => setShowAdd(s=>!s)}>
          {showAdd ? "✕ Cancel" : "+ Add Service"}
        </button>
      </div>

      {/* Add new service form */}
      {showAdd && (
        <div className="svc-add-form">
          <div className="svc-add-row">
            <div className="svc-field">
              <label>Icon</label>
              <div className="icon-picker">
                {ICON_OPTIONS.map(ic => (
                  <button key={ic} type="button"
                    className={`icon-opt ${newSvc.icon===ic?"selected":""}`}
                    onClick={() => setNewSvc(s=>({...s,icon:ic}))}
                  >{ic}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="svc-add-row">
            <div className="svc-field">
              <label>Service Name *</label>
              <input type="text" placeholder="e.g. Teeth Whitening"
                value={newSvc.name} onChange={e=>setNewSvc(s=>({...s,name:e.target.value}))} />
            </div>
            <div className="svc-field">
              <label>Duration</label>
              <input type="text" placeholder="e.g. 45 min"
                value={newSvc.duration} onChange={e=>setNewSvc(s=>({...s,duration:e.target.value}))} />
            </div>
            <div className="svc-field">
              <label>Price</label>
              <input type="text" placeholder="e.g. ₹3,000+"
                value={newSvc.price} onChange={e=>setNewSvc(s=>({...s,price:e.target.value}))} />
            </div>
          </div>
          <button className="svc-save-btn" onClick={addService} disabled={!newSvc.name.trim()}>
            + Add Service
          </button>
        </div>
      )}

      {/* Services list */}
      <div className="svc-list">
        {services.map(svc => (
          <ServiceRow key={svc.id} svc={svc}
            editing={editing===svc.id}
            onEdit={() => setEditing(svc.id)}
            onSave={(updated) => saveEdit(svc.id, updated)}
            onCancelEdit={() => setEditing(null)}
            onToggle={() => toggleActive(svc.id)}
            onDelete={() => deleteService(svc.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ServiceRow({ svc, editing, onEdit, onSave, onCancelEdit, onToggle, onDelete }) {
  const [draft, setDraft] = useState({ name:svc.name, icon:svc.icon, duration:svc.duration, price:svc.price });

  if (editing) {
    return (
      <div className="svc-row editing">
        <div className="edit-icon-row">
          {ICON_OPTIONS.map(ic => (
            <button key={ic} type="button"
              className={`icon-opt ${draft.icon===ic?"selected":""}`}
              onClick={() => setDraft(d=>({...d,icon:ic}))}
            >{ic}</button>
          ))}
        </div>
        <div className="edit-fields">
          <input value={draft.name} onChange={e=>setDraft(d=>({...d,name:e.target.value}))} placeholder="Service name" />
          <input value={draft.duration} onChange={e=>setDraft(d=>({...d,duration:e.target.value}))} placeholder="Duration" />
          <input value={draft.price} onChange={e=>setDraft(d=>({...d,price:e.target.value}))} placeholder="Price" />
        </div>
        <div className="edit-actions">
          <button className="act-btn act-confirm" onClick={() => onSave(draft)}>Save</button>
          <button className="act-btn act-cancel"  onClick={onCancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`svc-row ${!svc.active?"svc-inactive":""}`}>
      <div className="svc-icon-wrap">{svc.icon}</div>
      <div className="svc-info">
        <div className="svc-name">{svc.name}</div>
        <div className="svc-meta">
          {svc.duration && <span>⏱ {svc.duration}</span>}
          {svc.price    && <span>💰 {svc.price}</span>}
        </div>
      </div>
      <div className="svc-status-toggle">
        <label className="toggle-switch">
          <input type="checkbox" checked={svc.active} onChange={onToggle} />
          <span className="toggle-track"><span className="toggle-thumb"/></span>
        </label>
        <span className="toggle-label">{svc.active ? "Active" : "Hidden"}</span>
      </div>
      <div className="svc-row-actions">
        <button className="svc-act edit"   onClick={onEdit}>✏️ Edit</button>
        <button className="svc-act delete" onClick={onDelete}>🗑 Remove</button>
      </div>
    </div>
  );
}

/* ─── Main Dashboard ─── */
export default function AdminDashboard({ onLogout }) {
  const [appointments, setAppointments] = useState([]);
const fetchAppointments = async () => {
  try {
    const token = localStorage.getItem("adminToken");

    const res = await fetch(`${process.env.REACT_APP_API_URL}/appointments`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const data = await res.json();
    console.log("APPOINTMENTS RESPONSE:", data);

    if (!res.ok) {
      throw new Error(data.error || "Failed to fetch appointments");
    }

    const formatted = data.map((a) => ({
      id: a._id,
      name: a.name,
      email: a.email,
      phone: a.phone,
      service: a.service,
      reason: a.reason,
      message: a.message,
      date: a.date || "No date",
      slots: a.slots || [],
      status: a.status || "pending",
      confirmedSlot: a.confirmedSlot || null,
      createdAt: a.createdAt
    }));

    setAppointments(formatted);
  } catch (err) {
    console.error("FETCH APPOINTMENTS ERROR:", err);
  }
};
useEffect(() => {
  fetchAppointments();

  const interval = setInterval(() => {
    fetchAppointments();
  }, 5000);

  return () => clearInterval(interval);
}, []);
  const [activeTab,    setActiveTab]    = useState("dashboard");
  const [apptFilter,   setApptFilter]   = useState("all");
  const [search,       setSearch]       = useState("");
  const [sortBy,       setSortBy]       = useState("newest");
  const [toast,        setToast]        = useState(null);

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

const confirmAppt = async (id, slot) => {
  if (!slot) {
    showToast("Please select a time slot first", "error");
    return;
  }

  try {
    const token = localStorage.getItem("adminToken");

    const res = await fetch(`${process.env.REACT_APP_API_URL}/appointment/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        status: "confirmed",
        confirmedSlot: slot
      })
    });

    const data = await res.json();

    if (!res.ok) {
      showToast(data.error || "Error confirming appointment", "error");
      return;
    }

    showToast(`Appointment confirmed for ${slot}`);
    await fetchAppointments();
  } catch (err) {
    console.error(err);
    showToast("Error confirming appointment", "error");
  }
};

const cancelAppt = async (id) => {
  try {
    const token = localStorage.getItem("adminToken");

    const res = await fetch(`${process.env.REACT_APP_API_URL}/appointment/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        status: "cancelled",
        confirmedSlot: null
      })
    });

    const data = await res.json();

    if (!res.ok) {
      showToast(data.error || "Error cancelling appointment", "error");
      return;
    }

    showToast("Appointment cancelled", "error");
    await fetchAppointments();
  } catch (err) {
    console.error(err);
    showToast("Error cancelling appointment", "error");
  }
};

  const stats = useMemo(() => ({
    total:     appointments.length,
    pending:   appointments.filter(a=>a.status==="pending").length,
    confirmed: appointments.filter(a=>a.status==="confirmed").length,
    cancelled: appointments.filter(a=>a.status==="cancelled").length,
  }), [appointments]);

  // Service breakdown for charts
  const serviceBreakdown = useMemo(() => {
    const map = {};
    appointments.forEach(a => { map[a.service] = (map[a.service]||0) + 1; });
    return Object.entries(map)
      .map(([name,value]) => ({ name, value, ...(serviceColors[name]||serviceColors["Other"]) }))
      .sort((a,b) => b.value - a.value);
  }, [appointments]);

  const filtered = useMemo(() => {
    let list = appointments;
    if (apptFilter !== "all") list = list.filter(a => a.status===apptFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        a.phone.includes(q) ||
        a.email.includes(q) ||
        a.service.toLowerCase().includes(q) ||
        a.id.toLowerCase().includes(q)
      );
    }
    const sorts = {
      newest: (a,b) => new Date(b.createdAt)-new Date(a.createdAt),
      oldest: (a,b) => new Date(a.createdAt)-new Date(b.createdAt),
      date:   (a,b) => a.date.localeCompare(b.date),
      name:   (a,b) => a.name.localeCompare(b.name),
    };
    return [...list].sort(sorts[sortBy]);
  }, [appointments, apptFilter, search, sortBy]);

  const navItems = [
    { id:"dashboard",    icon:"📊", label:"Dashboard"    },
    { id:"appointments", icon:"📋", label:"Appointments", count: stats.total    },
    { id:"pending",      icon:"⏳", label:"Pending",      count: stats.pending  },
    { id:"confirmed",    icon:"✅", label:"Confirmed",    count: stats.confirmed },
    { id:"cancelled",    icon:"❌", label:"Cancelled",    count: stats.cancelled },
    { id:"services",     icon:"🦷", label:"Services"      },
  ];

  return (
    <div className="admin-dashboard">
      {toast && <div className={`admin-toast ${toast.type}`}>{toast.type==="success"?"✓":"✕"} {toast.msg}</div>}

      {/* ── Sidebar ── */}
      <aside className="admin-sidebar">
        <div className="sidebar-logo">
          <div className="sb-logo-icon">
            <svg width="22" height="22" viewBox="0 0 28 28" fill="none">
              <path d="M14 3C10 3 7 6.5 7 10.5c0 2 .8 3.8 2 5L14 25l5-9.5c1.2-1.2 2-3 2-5C21 6.5 18 3 14 3z" fill="#fff" fillOpacity=".95"/>
              <ellipse cx="11" cy="9" rx="2.5" ry="1.4" fill="white" fillOpacity=".4"/>
            </svg>
          </div>
          <div>
            <div className="sb-clinic">Krishna Dental</div>
            <div className="sb-portal">Admin Panel</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button key={item.id}
              className={`sb-nav-item ${activeTab===item.id?"active":""}`}
              onClick={() => { setActiveTab(item.id); if(["pending","confirmed","cancelled"].includes(item.id)) setApptFilter(item.id); else if(item.id==="appointments") setApptFilter("all"); }}
            >
              <span className="sb-icon">{item.icon}</span>
              <span className="sb-label">{item.label}</span>
              {item.count !== undefined && <span className="sb-count">{item.count}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="admin-user">
            <div className="admin-avatar">A</div>
            <div>
              <div className="admin-user-name">Administrator</div>
              <div className="admin-user-email">admin@krishnadental.com</div>
            </div>
          </div>
          <div className="sidebar-bottom-links">
            <a href="#home" className="sb-website-link" onClick={() => { window.location.hash=""; }}>← View Website</a>
            <button className="logout-btn" onClick={onLogout}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="admin-main">

        {/* ══ DASHBOARD TAB ══ */}
        {activeTab === "dashboard" && (
          <div className="dashboard-view">
            <div className="admin-header">
              <div>
                <h1 className="admin-title">Dashboard</h1>
                <p className="admin-subtitle">Overview of clinic activity</p>
              </div>
            </div>

            {/* Stat cards */}
            <div className="stats-row">
              {[
                { icon:"📋", label:"Total Requests",  value:stats.total,     color:"#0a7c6e", sub:"All time" },
                { icon:"⏳", label:"Pending Action",  value:stats.pending,   color:"#d48806", sub:"Needs attention" },
                { icon:"✅", label:"Confirmed",        value:stats.confirmed, color:"#1a8a1a", sub:"Booked in" },
                { icon:"❌", label:"Cancelled",        value:stats.cancelled, color:"#a81a1a", sub:"Declined" },
              ].map(s => (
                <div key={s.label} className="stat-card" style={{ borderTop:`4px solid ${s.color}` }}
                  onClick={() => { setActiveTab("appointments"); setApptFilter("all"); }}
                >
                  <div className="stat-icon" style={{ background:s.color+"18" }}>{s.icon}</div>
                  <div className="stat-body">
                    <div className="stat-value" style={{ color:s.color }}>{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-sub">{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div className="charts-row">
              {/* Donut */}
              <div className="chart-card">
                <h3 className="chart-title">Appointment Status</h3>
                <div className="donut-wrap">
                  <DonutChart segments={[
                    { value:stats.pending,   color:"#d48806" },
                    { value:stats.confirmed, color:"#1a8a1a" },
                    { value:stats.cancelled, color:"#a81a1a" },
                  ]} />
                  <div className="donut-legend">
                    {[
                      { label:"Pending",   value:stats.pending,   color:"#d48806" },
                      { label:"Confirmed", value:stats.confirmed, color:"#1a8a1a" },
                      { label:"Cancelled", value:stats.cancelled, color:"#a81a1a" },
                    ].map(l => (
                      <div key={l.label} className="donut-leg-item">
                        <span className="donut-dot" style={{ background:l.color }} />
                        <span>{l.label}</span>
                        <strong>{l.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bar chart — service breakdown */}
              <div className="chart-card chart-card-wide">
                <h3 className="chart-title">Requests by Service</h3>
                <BarChart
                  height={160}
                  data={serviceBreakdown.slice(0,7).map(s => ({
                    label: s.name.split(" ")[0],
                    value: s.value,
                    color: s.color,
                  }))}
                />
              </div>
            </div>

            {/* Recent pending */}
            <div className="chart-card" style={{ marginTop:0 }}>
              <div className="recent-header">
                <h3 className="chart-title" style={{margin:0}}>Recent Pending</h3>
                <button className="view-all-btn" onClick={() => { setActiveTab("pending"); setApptFilter("pending"); }}>
                  View all →
                </button>
              </div>
              <div className="recent-list">
                {appointments.filter(a=>a.status==="pending").slice(0,4).map(a => {
                  const sc = serviceColors[a.service]||serviceColors["Other"];
                  return (
                    <div key={a.id} className="recent-item">
                      <div className="recent-avatar">{a.name[0]}</div>
                      <div className="recent-info">
                        <div className="recent-name">{a.name}</div>
                        <div className="recent-detail">{a.service} · {a.date}</div>
                      </div>
                      <span className="service-badge" style={{ background:sc.bg, color:sc.color }}>{a.service}</span>
                      <div className="recent-actions">
                        <button className="act-btn act-confirm" onClick={() => confirmAppt(a.id, a.slots[0])}>✓</button>
                        <button className="act-btn act-cancel"  onClick={() => cancelAppt(a.id)}>✕</button>
                      </div>
                    </div>
                  );
                })}
                {appointments.filter(a=>a.status==="pending").length===0 && (
                  <div className="no-pending">🎉 No pending appointments!</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══ APPOINTMENTS TAB ══ */}
        {["appointments","pending","confirmed","cancelled"].includes(activeTab) && (
          <div>
            <div className="admin-header">
              <div>
                <h1 className="admin-title">
                  {apptFilter==="all" ? "All Appointments" : `${STATUS_LABELS[apptFilter]} Appointments`}
                </h1>
                <p className="admin-subtitle">{filtered.length} record{filtered.length!==1?"s":""} found</p>
              </div>
              <div className="admin-header-right">
                <div className="admin-search">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <input type="text" placeholder="Search name, phone, ID…" value={search} onChange={e=>setSearch(e.target.value)} />
                </div>
                <select className="admin-sort" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="date">By appt. date</option>
                  <option value="name">By name</option>
                </select>
              </div>
            </div>

            <div className="apt-table-wrap">
              {filtered.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>No appointments found</h3>
                  <p>Try adjusting your search or filter.</p>
                </div>
              ) : (
                <table className="apt-table">
                  <thead>
                    <tr>
                      <th>ID</th><th>Patient</th><th>Service</th>
                      <th>Date & Slots</th><th>Status</th><th>Received</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(appt => (
                      <AppointmentRow key={appt.id} appt={appt} onConfirm={confirmAppt} onCancel={cancelAppt} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ══ SERVICES TAB ══ */}
        {activeTab === "services" && <ServicesManager />}
      </main>
    </div>
  );
}
